import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquareShare, Search, Trophy, Users, Save, Trash2, Plus, Tag, EyeOff, Ban, Gift, BarChart3, Download, ShieldAlert, TrendingUp } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner, Toggle } from "@/components/kit";

const RANGES = [
  { key: "all", label: "All Time" },
  { key: "monthly", label: "Monthly" },
  { key: "weekly", label: "Weekly" },
];
const LIMITS = [10, 25, 50, 100];
const TABS = [
  { key: "board", label: "Leaderboard" },
  { key: "analytics", label: "Analytics" },
  { key: "giveaways", label: "Giveaways" },
  { key: "settings", label: "Settings" },
  { key: "labels", label: "Labels" },
];
const medals = ["🥇", "🥈", "🥉"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Invites() {
  const { guildId, guild, refreshKey } = useGuild();
  const locked = (guild?.plan || "free") === "free";
  const [tab, setTab] = useState("board");
  const [roles, setRoles] = useState([]);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    if (locked) return;
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
    api.get(`/guilds/${guildId}/discord/channels`).then((r) => setChannels(r.data.channels || [])).catch(() => {});
  }, [guildId, locked]);

  return (
    <div data-testid="invites-page">
      <PageHead title="Invites" desc="Track invites in real time, reward top inviters, and run a live leaderboard." />
      <PremiumBanner guildId={guildId} tier="Premium" locked={locked} />

      {!locked && (
        <>
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1 mb-6 w-fit">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`invites-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`text-sm px-4 py-1.5 rounded-md transition-colors ${tab === t.key ? "bg-volt text-volt-ink font-semibold" : "text-zinc-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
          {tab === "board" && <Leaderboard guildId={guildId} refreshKey={refreshKey} />}
          {tab === "analytics" && <Analytics guildId={guildId} />}
          {tab === "giveaways" && <Giveaways guildId={guildId} channels={channels} roles={roles} />}
          {tab === "settings" && <Settings guildId={guildId} roles={roles} channels={channels} />}
          {tab === "labels" && <Labels guildId={guildId} roles={roles} />}
        </>
      )}
    </div>
  );
}

/* ---------------- Leaderboard ---------------- */
function Leaderboard({ guildId, refreshKey }) {
  const [range, setRange] = useState("all");
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/guilds/${guildId}/invites/leaderboard`, { params: { range, limit } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], total_tracked: 0, count: 0 }))
      .finally(() => setLoading(false));
  }, [guildId, range, limit]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = (data?.rows || []).filter((r) =>
    !search || (r.username || r.user_id).toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat icon={Trophy} label="Ranked inviters" value={data?.count ?? "—"} testid="invites-stat-count" />
        <Stat icon={MessageSquareShare} label="Tracked invites" value={data?.total_tracked ?? "—"} testid="invites-stat-tracked" />
        <Stat icon={Users} label="Top inviter" value={rows[0]?.username || rows[0]?.user_id || "—"} testid="invites-stat-top" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`invites-range-${r.key}`} onClick={() => setRange(r.key)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${range === r.key ? "bg-volt text-volt-ink font-semibold" : "text-zinc-400 hover:text-white"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <select data-testid="invites-limit" value={limit} onChange={(e) => setLimit(Number(e.target.value))}
          className="bg-zinc-900 border border-zinc-800 rounded-lg text-sm px-3 py-2 outline-none focus:border-volt">
          {LIMITS.map((l) => <option key={l} value={l}>Top {l}</option>)}
        </select>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="invites-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search member…"
            className="bg-zinc-900 border border-zinc-800 rounded-lg text-sm pl-9 pr-3 py-2 outline-none focus:border-volt w-52" />
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <div className="col-span-1">#</div><div className="col-span-5">Member</div>
          <div className="col-span-1 text-right">Total</div><div className="col-span-1 text-right">Regular</div>
          <div className="col-span-1 text-right">Left</div><div className="col-span-1 text-right">Fake</div>
          <div className="col-span-2 text-right">Bonus</div>
        </div>
        {loading && <div className="px-4 py-10 text-center text-zinc-500 text-sm">Loading leaderboard…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-12 text-center text-zinc-500 text-sm" data-testid="invites-empty">
            No invites tracked yet. Once members join through invite links, they'll appear here.
          </div>
        )}
        {!loading && rows.map((r, i) => (
          <div key={r.user_id} data-testid="invites-row" className="grid grid-cols-12 gap-2 px-4 py-3 text-sm items-center border-b border-zinc-800/60 hover:bg-zinc-800/30">
            <div className="col-span-1 font-semibold">{i < 3 ? medals[i] : `#${i + 1}`}</div>
            <div className="col-span-5 flex items-center gap-2 min-w-0">
              {r.avatar && <img src={r.avatar} className="w-6 h-6 rounded-full bg-zinc-800" alt="" />}
              <span className="truncate">{r.username || <span className="text-zinc-500">User {r.user_id}</span>}</span>
            </div>
            <div className="col-span-1 text-right font-bold text-volt">{r.total}</div>
            <div className="col-span-1 text-right text-zinc-300">{r.regular}</div>
            <div className="col-span-1 text-right text-zinc-500">{r.left}</div>
            <div className="col-span-1 text-right text-red-400">{r.fake}</div>
            <div className="col-span-2 text-right text-cyan-400">{r.bonus}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- Analytics & Security ---------------- */
const SEV = { high: "bg-red-500/15 text-red-400 border-red-500/30", medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", low: "bg-zinc-700/40 text-zinc-300 border-zinc-600" };

function Analytics({ guildId }) {
  const [data, setData] = useState(null);
  const [sec, setSec] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api.get(`/guilds/${guildId}/invites/analytics`, { params: { days } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [guildId, days]);
  useEffect(() => { api.get(`/guilds/${guildId}/invites/security`).then((r) => setSec(r.data)).catch(() => setSec(null)); }, [guildId]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/guilds/${guildId}/invites/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "invite-stats.csv"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  if (!data) return <div className="text-zinc-500 text-sm">Loading analytics…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`analytics-days-${d}`} onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${days === d ? "bg-volt text-volt-ink font-semibold" : "text-zinc-400 hover:text-white"}`}>{d}d</button>
          ))}
        </div>
        <button data-testid="analytics-export" onClick={exportCsv} className="ml-auto text-sm border border-zinc-700 text-zinc-300 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-zinc-800"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Joins tracked" value={t.total_joins} testid="an-joins" />
        <Stat icon={ShieldAlert} label="Fake rate" value={`${t.fake_pct}%`} testid="an-fake" />
        <Stat icon={TrendingUp} label="Retention" value={`${t.retention_pct}%`} testid="an-retention" />
        <Stat icon={BarChart3} label="Rejoin rate" value={`${t.rejoin_pct}%`} testid="an-rejoin" />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="text-sm font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-volt" /> Invites over the last {t.days} days</div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.series} margin={{ left: -20, right: 10 }}>
            <defs>
              <linearGradient id="gJoin" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.5} /><stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient>
              <linearGradient id="gFake" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f87171" stopOpacity={0.4} /><stop offset="100%" stopColor="#f87171" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.2} />
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
            <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "rgb(var(--z-900))", border: "1px solid rgb(var(--z-800))", borderRadius: 8, fontSize: 12 }} />
            <Area type="monotone" dataKey="joins" stroke="#0EA5E9" fill="url(#gJoin)" name="Joins" strokeWidth={2} />
            <Area type="monotone" dataKey="fakes" stroke="#f87171" fill="url(#gFake)" name="Fakes" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Tag className="w-4 h-4 text-volt" /> Top invite links</div>
          {data.top_links.length === 0 && <p className="text-xs text-zinc-600">No invites tracked yet.</p>}
          <div className="space-y-2">
            {data.top_links.map((l) => (
              <div key={l.code} data-testid="an-top-link" className="flex items-center gap-2 text-sm bg-black/30 rounded px-3 py-2">
                <span className="font-mono text-volt">{l.vanity ? "vanity" : l.code}</span>
                {l.label && <span className="text-xs bg-zinc-800 text-zinc-300 rounded-full px-2 py-0.5">{l.label}</span>}
                <span className="text-zinc-500 text-xs truncate">{l.inviter_name}</span>
                <span className="ml-auto font-semibold">{l.uses} uses</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-400" /> Security & abuse</div>
          {!sec && <p className="text-xs text-zinc-600">Loading…</p>}
          {sec?.clean && <p className="text-sm text-emerald-400" data-testid="an-sec-clean">✓ No suspicious invite activity detected.</p>}
          <div className="space-y-2">
            {sec?.flags?.map((f, i) => (
              <div key={i} data-testid="an-sec-flag" className={`text-sm rounded-lg border px-3 py-2 ${SEV[f.severity] || SEV.low}`}>
                <div className="font-semibold text-xs uppercase tracking-wide">{f.type}</div>
                <div className="text-zinc-300 text-xs mt-0.5">{f.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Giveaways ---------------- */
const WINNER_MODES = [
  { v: "top1", l: "Rank #1" }, { v: "top3", l: "Top 3" }, { v: "top5", l: "Top 5" },
  { v: "top10", l: "Top 10" }, { v: "random", l: "Random winner(s)" },
];
const STATUS_STYLE = {
  active: "bg-volt/15 text-volt", paused: "bg-yellow-500/15 text-yellow-400",
  ended: "bg-zinc-700/40 text-zinc-300", cancelled: "bg-red-500/15 text-red-400", draft: "bg-zinc-800 text-zinc-500",
};
const BLANK_GV = { title: "", prize: "", description: "", banner_url: "", ends_at: "",
  timezone: "UTC", winner_mode: "top1", winner_count: 1, min_invites: 0, announce_channel: "", ping_role: "", status: "active" };

function Giveaways({ guildId, channels, roles = [] }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get(`/guilds/${guildId}/invite-giveaways`).then((r) => setState(r.data)).catch(() => setState({ giveaways: [], giveaways_enabled: true })), [guildId]);
  useEffect(() => { load(); }, [load]);
  if (!state) return <div className="text-zinc-500 text-sm">Loading giveaways…</div>;
  if (!state.giveaways_enabled)
    return <div data-testid="giveaways-disabled" className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-10 text-center text-zinc-400 text-sm">Giveaways are turned off. Enable them in the <b className="text-zinc-200">Settings</b> tab.</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.title.trim() || !form.prize.trim() || !form.ends_at) { toast.error("Title, prize and end date are required."); return; }
    const body = { ...form, ends_at: new Date(form.ends_at).toISOString() };
    try {
      if (form.id) await api.put(`/guilds/${guildId}/invite-giveaways/${form.id}`, body);
      else await api.post(`/guilds/${guildId}/invite-giveaways`, body);
      toast.success(form.id ? "Giveaway updated" : "Giveaway created"); setForm(null); load();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const act = async (gv, action) => {
    if (action === "cancel" && !window.confirm("Cancel this giveaway?")) return;
    try { await api.post(`/guilds/${guildId}/invite-giveaways/${gv.id}/action`, { action }); toast.success(`Giveaway ${action}`); load(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const del = async (gv) => { if (!window.confirm("Delete this giveaway permanently?")) return; try { await api.delete(`/guilds/${guildId}/invite-giveaways/${gv.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } };
  const edit = (gv) => setForm({ ...gv, ends_at: gv.ends_at ? gv.ends_at.slice(0, 16) : "" });

  return (
    <div className="space-y-5 max-w-4xl">
      {!form && (
        <button data-testid="giveaway-new" onClick={() => setForm({ ...BLANK_GV })} className="bg-volt text-volt-ink font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2 hover:brightness-110"><Plus className="w-4 h-4" /> New giveaway</button>
      )}

      {form && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4" data-testid="giveaway-form">
          <h3 className="font-head font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-volt" /> {form.id ? "Edit giveaway" : "Create giveaway"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title"><input data-testid="gv-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} placeholder="Summer Invite Contest" /></Field>
            <Field label="Prize"><input data-testid="gv-prize" value={form.prize} onChange={(e) => set("prize", e.target.value)} className={inp} placeholder="£50 Steam Gift Card" /></Field>
          </div>
          <Field label="Description"><textarea data-testid="gv-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className={inp + " min-h-[70px]"} placeholder="Invite the most people to win!" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Banner image URL"><input data-testid="gv-banner" value={form.banner_url} onChange={(e) => set("banner_url", e.target.value)} className={inp} placeholder="https://…" /></Field>
            <Field label="Ends at (your local time)"><input data-testid="gv-ends" type="datetime-local" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} className={inp} /></Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Winner selection"><select data-testid="gv-mode" value={form.winner_mode} onChange={(e) => set("winner_mode", e.target.value)} className={inp}>{WINNER_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></Field>
            {form.winner_mode === "random" && <Field label="Number of winners"><input data-testid="gv-count" type="number" min="1" value={form.winner_count} onChange={(e) => set("winner_count", Number(e.target.value))} className={inp} /></Field>}
            <Field label="Min. invites to enter"><input data-testid="gv-min" type="number" min="0" value={form.min_invites} onChange={(e) => set("min_invites", Number(e.target.value))} className={inp} /></Field>
          </div>
          <Field label="Announce channel"><select data-testid="gv-channel" value={form.announce_channel} onChange={(e) => set("announce_channel", e.target.value)} className={inp}><option value="">— server default —</option>{channels.filter((c) => c.type === 0 || c.type === undefined).map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}</select></Field>
          <Field label="Ping role (optional)"><select data-testid="gv-ping" value={form.ping_role || ""} onChange={(e) => set("ping_role", e.target.value)} className={inp}><option value="">— no ping —</option><option value="everyone">@everyone</option><option value="here">@here</option>{roles.map((r) => <option key={r.id} value={r.id}>@{r.name}</option>)}</select></Field>
          <div className="flex gap-2">
            <button data-testid="gv-save" onClick={submit} className="bg-volt text-volt-ink font-semibold rounded-lg px-5 py-2 flex items-center gap-2 hover:brightness-110"><Save className="w-4 h-4" /> {form.id ? "Save" : "Create"}</button>
            <button data-testid="gv-cancel" onClick={() => setForm(null)} className="border border-zinc-700 text-zinc-300 rounded-lg px-4 py-2 hover:bg-zinc-800">Cancel</button>
          </div>
        </div>
      )}

      {state.giveaways.length === 0 && !form && <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-10 text-center text-zinc-500 text-sm" data-testid="giveaways-empty">No giveaways yet — create one to reward your top inviters.</div>}
      {state.giveaways.map((gv) => (
        <div key={gv.id} data-testid="giveaway-card" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Gift className="w-5 h-5 text-volt mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-head font-semibold">{gv.title}</span>
                <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[gv.status] || STATUS_STYLE.draft}`}>{gv.status}</span>
              </div>
              <div className="text-sm text-zinc-400">🏆 {gv.prize} · {WINNER_MODES.find((m) => m.v === gv.winner_mode)?.l} · min {gv.min_invites} invites</div>
              <div className="text-xs text-zinc-600 mt-0.5">Ends {gv.ends_at ? new Date(gv.ends_at).toLocaleString() : "—"}</div>
              {gv.winners?.length > 0 && <div className="text-xs text-volt mt-1">🎉 Winners: {gv.winners.map((w) => w.username || w.user_id).join(", ")}</div>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 pl-8">
            {gv.status === "active" && <Act tid="gv-pause" onClick={() => act(gv, "pause")}>Pause</Act>}
            {gv.status === "paused" && <Act tid="gv-resume" onClick={() => act(gv, "resume")}>Resume</Act>}
            {["active", "paused"].includes(gv.status) && <Act tid="gv-end" onClick={() => act(gv, "end")}>End now</Act>}
            {gv.status === "ended" && <Act tid="gv-reroll" onClick={() => act(gv, "reroll")}>Reroll</Act>}
            {gv.status !== "ended" && gv.status !== "cancelled" && <Act tid="gv-edit" onClick={() => edit(gv)}>Edit</Act>}
            <Act tid="gv-duplicate" onClick={() => act(gv, "duplicate")}>Duplicate</Act>
            {["active", "paused", "draft"].includes(gv.status) && <Act tid="gv-cancel" danger onClick={() => act(gv, "cancel")}>Cancel</Act>}
            <Act tid="gv-delete" danger onClick={() => del(gv)}>Delete</Act>
          </div>
        </div>
      ))}
    </div>
  );
}
const inp = "w-full bg-black border border-zinc-800 rounded px-3 py-2 text-sm outline-none focus:border-volt";
const Field = ({ label, children }) => (<label className="block"><span className="text-xs text-zinc-500 mb-1 block">{label}</span>{children}</label>);
const Act = ({ children, onClick, tid, danger }) => (
  <button data-testid={tid} onClick={onClick} className={`text-xs rounded-md px-3 py-1.5 border transition-colors ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>{children}</button>
);

/* ---------------- Settings ---------------- */
function Settings({ guildId, roles, channels }) {
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [newHidden, setNewHidden] = useState("");
  const [blUser, setBlUser] = useState("");
  const [blReason, setBlReason] = useState("");

  useEffect(() => { api.get(`/guilds/${guildId}/invites/settings`).then((r) => setCfg(r.data)).catch(() => {}); }, [guildId]);
  if (!cfg) return <div className="text-zinc-500 text-sm">Loading settings…</div>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const toggleRole = (id) => set("role_blacklist",
    (cfg.role_blacklist || []).includes(id) ? cfg.role_blacklist.filter((x) => x !== id) : [...(cfg.role_blacklist || []), id]);
  const addHidden = () => { if (!newHidden.trim()) return; set("hidden_users", [...new Set([...(cfg.hidden_users || []), newHidden.trim()])]); setNewHidden(""); };
  const addBlacklist = () => {
    if (!blUser.trim()) return;
    set("user_blacklist", [...(cfg.user_blacklist || []), { user_id: blUser.trim(), reason: blReason.trim() }]);
    setBlUser(""); setBlReason("");
  };

  const saveRaw = async () => {
    try {
      const { data } = await api.put(`/guilds/${guildId}/invites/settings`, cfg);
      setCfg(data); toast.success("Invite settings saved");
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <Row title="Enable invite tracking" desc="Master switch for tracking, leaderboard and rewards.">
          <Toggle testid="inv-set-enabled" checked={!!cfg.enabled} onChange={(v) => set("enabled", v)} />
        </Row>
        <Row title="Fake invite delay" desc="Accounts younger than this many days count as fake.">
          <input data-testid="inv-set-fakedelay" type="number" min="0" max="365" value={cfg.fake_delay_days}
            onChange={(e) => set("fake_delay_days", Number(e.target.value))}
            className="w-20 bg-black border border-zinc-800 rounded px-2 py-1.5 text-sm text-right outline-none focus:border-volt" />
        </Row>
        <Row title="Don't count rejoins" desc="Members who left and return count as fake invites.">
          <Toggle testid="inv-set-rejoins" checked={!!cfg.dont_count_rejoins} onChange={(v) => set("dont_count_rejoins", v)} />
        </Row>
        <Row title="Notifications channel" desc="Where leaderboard-leader & giveaway alerts are posted.">
          <select data-testid="inv-set-notify" value={cfg.notify_channel || ""} onChange={(e) => set("notify_channel", e.target.value)}
            className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-sm outline-none focus:border-volt w-52">
            <option value="">— none —</option>
            {channels.filter((c) => c.type === 0 || c.type === undefined).map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </Row>
        <Row title="Giveaways" desc="Enable leaderboard-tied giveaways (turn off without affecting tracking).">
          <Toggle testid="inv-set-giveaways" checked={cfg.giveaways_enabled !== false} onChange={(v) => set("giveaways_enabled", v)} />
        </Row>
      </Card>

      <Card>
        <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-volt" /> Weekly leaderboard auto-post</h3>
        <p className="text-xs text-zinc-500 mb-1">Let the bot drop a fresh Top-10 invite leaderboard embed in a channel once a week.</p>
        <Row title="Enable weekly auto-post" desc="Posts automatically on your chosen day each week.">
          <Toggle testid="inv-set-autopost" checked={!!cfg.leaderboard_autopost} onChange={(v) => set("leaderboard_autopost", v)} />
        </Row>
        <Row title="Post to channel" desc="Falls back to the notifications channel if left as default.">
          <select data-testid="inv-set-lbchannel" value={cfg.leaderboard_channel || ""} onChange={(e) => set("leaderboard_channel", e.target.value)}
            className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-sm outline-none focus:border-volt w-52">
            <option value="">— use notifications channel —</option>
            {channels.filter((c) => c.type === 0 || c.type === undefined).map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </Row>
        <Row title="Day of week" desc="When the leaderboard is posted each week.">
          <select data-testid="inv-set-lbday" value={cfg.leaderboard_day ?? 0} onChange={(e) => set("leaderboard_day", Number(e.target.value))}
            className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-sm outline-none focus:border-volt">
            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </Row>
        <Row title="Ping role (optional)" desc="Mentioned in the message content so members get notified.">
          <select data-testid="inv-set-lbping" value={cfg.leaderboard_ping_role || ""} onChange={(e) => set("leaderboard_ping_role", e.target.value)}
            className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-sm outline-none focus:border-volt w-52">
            <option value="">— no ping —</option>
            <option value="everyone">@everyone</option>
            <option value="here">@here</option>
            {roles.map((r) => <option key={r.id} value={r.id}>@{r.name}</option>)}
          </select>
        </Row>
      </Card>

      <Card>
        <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><Ban className="w-4 h-4 text-red-400" /> Role blacklist</h3>
        <p className="text-xs text-zinc-500 mb-3">Members with these roles can invite people, but their totals never increase.</p>
        <div className="flex flex-wrap gap-2">
          {roles.length === 0 && <span className="text-xs text-zinc-600">Link your server to load roles.</span>}
          {roles.map((r) => {
            const on = (cfg.role_blacklist || []).includes(r.id);
            return (
              <button key={r.id} data-testid="inv-role-bl" onClick={() => toggleRole(r.id)}
                className={`text-xs rounded-full px-3 py-1 border transition-colors ${on ? "bg-red-500/15 text-red-300 border-red-500/40" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                {r.name}
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><Ban className="w-4 h-4 text-red-400" /> User blacklist</h3>
        <p className="text-xs text-zinc-500 mb-3">Invites created by these users never count.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input data-testid="inv-bl-user" value={blUser} onChange={(e) => setBlUser(e.target.value)} placeholder="Discord user ID"
            className="bg-black border border-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:border-volt flex-1 min-w-[140px]" />
          <input data-testid="inv-bl-reason" value={blReason} onChange={(e) => setBlReason(e.target.value)} placeholder="Reason (optional)"
            className="bg-black border border-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:border-volt flex-1 min-w-[140px]" />
          <button data-testid="inv-bl-add" onClick={addBlacklist} className="text-sm bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 hover:bg-zinc-700 flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="space-y-1.5">
          {(cfg.user_blacklist || []).length === 0 && <p className="text-xs text-zinc-600">No blacklisted users.</p>}
          {(cfg.user_blacklist || []).map((b, i) => (
            <div key={i} data-testid="inv-bl-row" className="flex items-center gap-2 text-sm bg-black/30 rounded px-3 py-2">
              <span className="font-mono text-zinc-300">{b.user_id}</span>
              {b.reason && <span className="text-zinc-500 truncate">— {b.reason}</span>}
              <button data-testid="inv-bl-remove" onClick={() => set("user_blacklist", cfg.user_blacklist.filter((_, idx) => idx !== i))}
                className="ml-auto text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><EyeOff className="w-4 h-4 text-cyan-400" /> Hidden from leaderboard</h3>
        <p className="text-xs text-zinc-500 mb-3">These members keep earning invites but never appear on the public leaderboard.</p>
        <div className="flex gap-2 mb-3">
          <input data-testid="inv-hidden-input" value={newHidden} onChange={(e) => setNewHidden(e.target.value)} placeholder="Discord user ID"
            className="bg-black border border-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:border-volt flex-1" />
          <button data-testid="inv-hidden-add" onClick={addHidden} className="text-sm bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 hover:bg-zinc-700 flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(cfg.hidden_users || []).length === 0 && <p className="text-xs text-zinc-600">Nobody hidden.</p>}
          {(cfg.hidden_users || []).map((u) => (
            <span key={u} data-testid="inv-hidden-row" className="text-xs bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 rounded-full px-3 py-1 flex items-center gap-1.5">
              {u}
              <button onClick={() => set("hidden_users", cfg.hidden_users.filter((x) => x !== u))} className="hover:text-white">✕</button>
            </span>
          ))}
        </div>
      </Card>

      <button data-testid="inv-set-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2 hover:brightness-110">
        <Save className="w-4 h-4" /> Save settings
      </button>
    </div>
  );
}

/* ---------------- Labels ---------------- */
function Labels({ guildId, roles }) {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get(`/guilds/${guildId}/invites/list`).then((r) => setList(r.data)).catch(() => setList([])), [guildId]);
  useEffect(() => { load(); }, [load]);
  const setField = (code, k, v) => setList((l) => l.map((i) => i.code === code ? { ...i, [k]: v } : i));
  const save = async (inv) => {
    try { await api.put(`/guilds/${guildId}/invites/${inv.code}/label`, { label: inv.label, reward_role: inv.reward_role }); toast.success(`Saved “${inv.code}”`); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  if (!list) return <div className="text-zinc-500 text-sm">Loading invites…</div>;
  return (
    <div className="space-y-3 max-w-4xl">
      <p className="text-sm text-zinc-500 flex items-center gap-2"><Tag className="w-4 h-4" /> Label your invite links and auto-assign a reward role to everyone who joins through them.</p>
      {list.length === 0 && <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-10 text-center text-zinc-500 text-sm" data-testid="inv-labels-empty">No invites synced yet. Create an invite in Discord — it'll appear here after the bot syncs.</div>}
      {list.map((inv) => (
        <div key={inv.code} data-testid="inv-label-row" className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <div className="font-mono text-sm text-volt">{inv.vanity ? "vanity" : inv.code}</div>
            <div className="text-xs text-zinc-500">{inv.inviter_name || "—"} · {inv.uses} uses</div>
          </div>
          <input data-testid="inv-label-input" value={inv.label || ""} onChange={(e) => setField(inv.code, "label", e.target.value)} placeholder="Label (e.g. TikTok Campaign)"
            className="bg-black border border-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:border-volt flex-1 min-w-[160px]" />
          <select data-testid="inv-label-role" value={inv.reward_role || ""} onChange={(e) => setField(inv.code, "reward_role", e.target.value)}
            className="bg-black border border-zinc-800 rounded px-3 py-1.5 text-sm outline-none focus:border-volt w-48">
            <option value="">— no reward role —</option>
            {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
          <button data-testid="inv-label-save" onClick={() => save(inv)} className="text-sm bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 hover:bg-zinc-700 flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>
        </div>
      ))}
    </div>
  );
}

/* ---------------- small helpers ---------------- */
const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-volt/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-volt" /></div>
    <div className="min-w-0"><div className="text-xs text-zinc-500">{label}</div><div className="font-head font-bold truncate">{value}</div></div>
  </div>
);
const Card = ({ children }) => <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">{children}</div>;
const Row = ({ title, desc, children }) => (
  <div className="flex items-start justify-between gap-4">
    <div><div className="text-sm font-semibold">{title}</div><div className="text-xs text-zinc-500">{desc}</div></div>
    <div className="shrink-0">{children}</div>
  </div>
);
